/**
 * Annex B framing shared by the NAL-based codecs (H.264 and H.265).
 *
 * Annex B delimits NAL units with start codes; that framing is identical
 * across H.264 and H.265 — only the NAL header layout after each start code
 * differs (1-byte header with a 5-bit type vs. 2-byte header with a 6-bit
 * type). This module holds the codec-independent start-code scan; the
 * per-codec modules (`h264.ts`, `h265.ts`) interpret the headers.
 */

/**
 * Split Annex B-framed data into NAL unit payloads (header included, start
 * code excluded). Handles both 3-byte (`00 00 01`) and 4-byte (`00 00 00 01`)
 * start codes. Returned views alias the input buffer; they are not copies.
 */
export function scanNalUnitPayloads(data: Uint8Array): Uint8Array[] {
  // First locate every start code, then slice unit payloads between them.
  const boundaries: { start: number; dataStart: number }[] = [];
  let i = 0;
  while (i + 3 <= data.length) {
    if (data[i] !== 0x00 || data[i + 1] !== 0x00) {
      i += 1;
      continue;
    }
    if (data[i + 2] === 0x01) {
      boundaries.push({ start: i, dataStart: i + 3 });
      i += 3;
      continue;
    }
    if (data[i + 2] === 0x00 && data[i + 3] === 0x01) {
      boundaries.push({ start: i, dataStart: i + 4 });
      i += 4;
      continue;
    }
    i += 1;
  }

  const payloads: Uint8Array[] = [];
  for (let j = 0; j < boundaries.length; j++) {
    const end =
      j + 1 < boundaries.length ? boundaries[j + 1].start : data.length;
    const payload = data.subarray(boundaries[j].dataStart, end);
    if (payload.length > 0) {
      payloads.push(payload);
    }
  }
  return payloads;
}

/**
 * Undo emulation-prevention escaping on (a prefix of) a NAL unit payload:
 * every `00 00 03` in the raw byte stream encodes `00 00` in the RBSP.
 *
 * Only the first `limit` RBSP bytes are produced — callers reading
 * fixed-position header fields (e.g. the H.265 profile-tier-level) need a
 * short prefix, not the whole unescaped unit.
 */
export function unescapeRbsp(data: Uint8Array, limit: number): Uint8Array {
  const out = new Uint8Array(Math.min(limit, data.length));
  let outLength = 0;
  let zeroRun = 0;
  for (let i = 0; i < data.length && outLength < limit; i++) {
    const byte = data[i];
    if (zeroRun >= 2 && byte === 0x03) {
      zeroRun = 0;
      continue;
    }
    zeroRun = byte === 0x00 ? zeroRun + 1 : 0;
    out[outLength++] = byte;
  }
  return out.subarray(0, outLength);
}
